/**
 * File: references.ts
 * Path: packages/config/src/references/references.ts
 * Description: The one reference system for clients, contracts and the
 * payments made against them. Every reference IDK Solutions prints or asks a
 * client to use is built and read here, so changing the format is a change
 * to this file alone.
 *
 *   Client number      C012               assigned once, in order, never reused
 *   Contract reference IDK-C012-02        the client's second contract (or amendment)
 *   Payment reference  IDK-C012-02-DEP    what this payment is for
 *
 * Payment parts: DEP (deposit, the plan's first payment), SET (final
 * settlement), P1, P2... (Pay As We Build phases), M01, M02... (monthly
 * instalments and retainer months) and S1, S2... (FORGE build stages).
 *
 * A care plan added by hand has no contract, so its months are paid with
 * IDK-CP-<plan>-M03: "CP" for care plan and the first eight letters and
 * digits of the plan's id, so each plan's months have their own references.
 *
 * A payment reference never runs past 20 characters, the limit South African
 * banks give a beneficiary reference. Contracts drafted before this system
 * keep their old "IDK-2026-AB12CD" reference; their payment references
 * ("IDK-2026-AB12CD-DEP") still fit.
 */

/** Liam, 1 Oct 2026: one fixed sentence wherever a client is told how to pay. */
export const PAYMENT_REFERENCE_INSTRUCTION =
  "Use the reference shown for each payment exactly as written.";

/** The longest beneficiary reference South African banks accept. */
export const PAYMENT_REFERENCE_MAX_LENGTH = 20;

const BRAND = "IDK";
const CLIENT_PREFIX = "C";
/** C001: at least three digits, more once there are a thousand clients. */
const CLIENT_DIGITS = 3;
/** IDK-C012-02: at least two digits for the client's contract number. */
const CONTRACT_DIGITS = 2;
/** M01: at least two digits for a month. */
const MONTH_DIGITS = 2;

const isCount = (n: unknown): n is number =>
  typeof n === "number" && Number.isSafeInteger(n) && n >= 1;

const assertCount = (n: number, what: string): void => {
  if (!isCount(n)) {
    throw new RangeError(`${what} must be a whole number from 1, not ${n}.`);
  }
};

const pad = (n: number, digits: number): string =>
  n.toString().padStart(digits, "0");

/** "007" → 7; refuses "0", "-1", "1.5", "" and anything with letters. */
const readCount = (digits: string): number | null => {
  if (!/^\d+$/.test(digits)) return null;
  const n = Number(digits);
  return isCount(n) ? n : null;
};

// ─── Client numbers ──────────────────────────────────────────────────────

/** 12 → "C012". */
export const formatClientNumber = (n: number): string => {
  assertCount(n, "A client number");
  return `${CLIENT_PREFIX}${pad(n, CLIENT_DIGITS)}`;
};

const CLIENT_NUMBER = new RegExp(`^${CLIENT_PREFIX}(\\d{${CLIENT_DIGITS},})$`);

/** "C012" → 12; null for anything that is not a client number. */
export const parseClientNumber = (value: string): number | null => {
  const match = CLIENT_NUMBER.exec(value.trim());
  return match ? readCount(match[1]) : null;
};

// ─── Contract references ─────────────────────────────────────────────────

/** Client 12's second contract → "IDK-C012-02". */
export const formatContractReference = (
  clientNumber: number,
  contractNumber: number,
): string => {
  assertCount(contractNumber, "A contract number");
  return `${BRAND}-${formatClientNumber(clientNumber)}-${pad(contractNumber, CONTRACT_DIGITS)}`;
};

const CONTRACT_REFERENCE = new RegExp(
  `^${BRAND}-${CLIENT_PREFIX}(\\d{${CLIENT_DIGITS},})-(\\d{${CONTRACT_DIGITS},})$`,
);

export interface ParsedContractReference {
  clientNumber: number;
  contractNumber: number;
}

/** "IDK-C012-02" → { clientNumber: 12, contractNumber: 2 }. */
export const parseContractReference = (
  value: string,
): ParsedContractReference | null => {
  const match = CONTRACT_REFERENCE.exec(value.trim());
  if (!match) return null;
  const clientNumber = readCount(match[1]);
  const contractNumber = readCount(match[2]);
  return clientNumber && contractNumber
    ? { clientNumber, contractNumber }
    : null;
};

/** True for a reference made by this system (not a pre-October 2026 one). */
export const isContractReference = (value: string): boolean =>
  parseContractReference(value) !== null;

/**
 * The reference contracts were given before client numbers: "IDK-2026-AB12CD",
 * the year drafted and the start of the document id. Read-only now: it names
 * old contracts that have no client number, and is never issued again.
 */
export const legacyContractReference = (
  id: string,
  year = new Date().getUTCFullYear(),
): string =>
  `${BRAND}-${year}-${id
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 6)
    .toUpperCase()}`;

const LEGACY_REFERENCE = new RegExp(`^${BRAND}-\\d{4}-[A-Z0-9]{1,6}$`);

export const isLegacyContractReference = (value: string): boolean =>
  LEGACY_REFERENCE.test(value.trim());

// ─── Payment references ──────────────────────────────────────────────────

/** What a payment is for, as the payment plan's schedule names it. */
export type PaymentPart =
  | { kind: "deposit" }
  | { kind: "settlement" }
  | { kind: "phase"; n: number }
  | { kind: "month"; n: number }
  | { kind: "sprint"; n: number };

/** The deposit, the plan's first payment. */
export const DEPOSIT_PART: PaymentPart = { kind: "deposit" };
/** The final settlement. */
export const SETTLEMENT_PART: PaymentPart = { kind: "settlement" };
export const phasePart = (n: number): PaymentPart => ({ kind: "phase", n });
export const monthPart = (n: number): PaymentPart => ({ kind: "month", n });
export const sprintPart = (n: number): PaymentPart => ({ kind: "sprint", n });

const PART_CODES = {
  deposit: "DEP",
  settlement: "SET",
  phase: "P",
  month: "M",
  sprint: "S",
} as const;

/** { kind: "month", n: 3 } → "M03". */
export const formatPaymentPart = (part: PaymentPart): string => {
  switch (part.kind) {
    case "deposit":
      return PART_CODES.deposit;
    case "settlement":
      return PART_CODES.settlement;
    case "phase":
      assertCount(part.n, "A phase number");
      return `${PART_CODES.phase}${part.n}`;
    case "month":
      assertCount(part.n, "A month number");
      return `${PART_CODES.month}${pad(part.n, MONTH_DIGITS)}`;
    case "sprint":
      assertCount(part.n, "A stage number");
      return `${PART_CODES.sprint}${part.n}`;
  }
};

const PART_PATTERN = new RegExp(
  `^(?:(${PART_CODES.deposit})|(${PART_CODES.settlement})|${PART_CODES.phase}(\\d+)|${PART_CODES.month}(\\d{${MONTH_DIGITS},})|${PART_CODES.sprint}(\\d+))$`,
);

/** "M03" → { kind: "month", n: 3 }; null for anything else. */
export const parsePaymentPart = (value: string): PaymentPart | null => {
  const match = PART_PATTERN.exec(value);
  if (!match) return null;
  if (match[1]) return DEPOSIT_PART;
  if (match[2]) return SETTLEMENT_PART;
  const [digits, make] = match[3]
    ? [match[3], phasePart]
    : match[4]
      ? [match[4], monthPart]
      : [match[5] ?? "", sprintPart];
  const count = readCount(digits);
  if (count === null) return null;
  const part = make(count);
  // One spelling per payment: "P01" or "M003" would be a second way to
  // write P1 or M03, so only the formatted spelling reads back.
  return formatPaymentPart(part) === value ? part : null;
};

/**
 * "IDK-C012-02" + deposit → "IDK-C012-02-DEP". Throws rather than print a
 * reference a bank would cut short.
 */
export const formatPaymentReference = (
  contractReference: string,
  part: PaymentPart,
): string => {
  if (!contractReference.trim()) {
    throw new RangeError("A payment reference needs the contract's reference.");
  }
  const reference = `${contractReference.trim()}-${formatPaymentPart(part)}`;
  if (reference.length > PAYMENT_REFERENCE_MAX_LENGTH) {
    throw new RangeError(
      `${reference} is longer than the ${PAYMENT_REFERENCE_MAX_LENGTH} characters a bank reference allows.`,
    );
  }
  return reference;
};

export interface ParsedPaymentReference {
  contractReference: string;
  part: PaymentPart;
}

/** "IDK-C012-02-DEP" → its contract reference and what it pays for. */
export const parsePaymentReference = (
  value: string,
): ParsedPaymentReference | null => {
  const trimmed = value.trim();
  if (trimmed.length > PAYMENT_REFERENCE_MAX_LENGTH) return null;
  const cut = trimmed.lastIndexOf("-");
  if (cut <= 0) return null;
  const contractReference = trimmed.slice(0, cut);
  const part = parsePaymentPart(trimmed.slice(cut + 1));
  if (!part) return null;
  if (
    !isContractReference(contractReference) &&
    !isLegacyContractReference(contractReference)
  ) {
    return null;
  }
  return { contractReference, part };
};

// ─── Care plans with no contract ─────────────────────────────────────────

const CARE_PLAN_CODE = "CP";
/** How much of the plan's id goes into its references. */
const CARE_PLAN_ID_CHARS = 8;

/**
 * Month 3 of hand-added care plan "pondoTours7x" → "IDK-CP-PONDOTOU-M03".
 * Throws for a plan id with no letters or digits, or a reference a bank
 * would cut short (month 1000 and on).
 */
export const formatCarePlanReference = (planId: string, month: number): string => {
  const id = planId.replace(/[^a-zA-Z0-9]/g, "").slice(0, CARE_PLAN_ID_CHARS).toUpperCase();
  if (!id) throw new RangeError("A care plan reference needs the plan's id.");
  const reference = `${BRAND}-${CARE_PLAN_CODE}-${id}-${formatPaymentPart(monthPart(month))}`;
  if (reference.length > PAYMENT_REFERENCE_MAX_LENGTH) {
    throw new RangeError(
      `${reference} is longer than the ${PAYMENT_REFERENCE_MAX_LENGTH} characters a bank reference allows.`,
    );
  }
  return reference;
};
